import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../../header/header";
import Navvbar from "../../../header/Navvbar";
import Swal from "sweetalert2";
import "./AssignPrice.css";

const AssignPrice = () => {
  // =================== STATE MANAGEMENT ===================
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [priceGroups, setPriceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userLabId, setUserLabId] = useState(null);

  // =================== FETCH USER LAB ID FROM LOCALSTORAGE ===================
  useEffect(() => {
    const labId = localStorage.getItem("userLabId");
    setUserLabId(labId);
  }, []);

  // =================== FETCH ACCOUNTS & PRICE GROUPS FROM SERVER ===================
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        const accountsUrl = "https://hms.backend.hiragroupcloud.com/api/account/account";

        let groupsUrl = "https://hms.backend.hiragroupcloud.com/api/prices/get";

        if (userLabId) {
          groupsUrl += `?userLabId=${encodeURIComponent(userLabId)}`;
        }

        const [accountsRes, groupsRes] = await Promise.all([
          fetch(accountsUrl),
          fetch(groupsUrl),
        ]);

        if (!accountsRes.ok || !groupsRes.ok) {
          throw new Error("Failed to fetch data from server");
        }

        const accountsData = await accountsRes.json();
        const groupsRaw = await groupsRes.json();

        if (!accountsData.data || !Array.isArray(accountsData.data)) {
          throw new Error("Invalid accounts data received");
        }

        if (!Array.isArray(groupsRaw)) {
          throw new Error("Invalid price groups data received");
        }

        setAllAccounts(accountsData.data);

        let filteredAccounts = accountsData.data;

        if (userLabId) {
          filteredAccounts = accountsData.data.filter(
            (account) => account.addedBy === userLabId
          );
        }

        const formattedAccounts = filteredAccounts.map((account) => {
          const accountGroups = account.priceGroups
            ? account.priceGroups
                .filter(
                  (g) =>
                    g &&
                    typeof g === "object" &&
                    "name" in g
                )
                .map((g) => g.name)
            : [];

          return {
            id: account._id,
            name:
              account.account_name ||
              `Account ${account.accountId}`,
            accountId: account.accountId,
            selected: `${accountGroups.length} Selected`,
            group: `(${accountGroups.join(", ")})`,
            groups: accountGroups,
          };
        });

        const sanitizedGroups = groupsRaw.map((group) => ({
          ...group,
          members: Array.isArray(group.members)
            ? group.members
            : [],
        }));

        setAccounts(formattedAccounts);
        setPriceGroups(sanitizedGroups);
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while loading data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [userLabId]);

  // =================== UPDATE GROUP MEMBERSHIP ON BACKEND ===================
  const updateGroupInBackend = async (
    groupName,
    accountId,
    action
  ) => {
    const group = priceGroups.find(
      (g) => g.name === groupName
    );

    if (!group) {
      throw new Error(
        `Group "${groupName}" not found`
      );
    }

    let updateUrl = `https://hms.backend.hiragroupcloud.com/api/prices/${group._id}`;

    if (userLabId) {
      updateUrl += `?userLabId=${encodeURIComponent(
        userLabId
      )}`;
    }

    const response = await fetch(updateUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action,
        accountId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        errorText ||
          `Failed to ${action} account from group`
      );
    }

    const updatedGroup = await response.json();

    setPriceGroups((prev) =>
      prev.map((g) =>
        g._id === group._id ? updatedGroup : g
      )
    );

    return updatedGroup;
  };

  // =================== OPEN MODAL TO ASSIGN GROUPS ===================
  const handleLinkClick = (account) => {
    setSelectedAccount(account);
    setSelectedGroups(account.groups || []);

    const allGroupNames = priceGroups.map(
      (g) => g.name
    );

    setAvailableGroups(
      allGroupNames.filter(
        (name) => !account.groups.includes(name)
      )
    );

    setIsModalOpen(true);
  };

  // =================== CLOSE MODAL ===================
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAccount(null);
    setSelectedGroups([]);
    setAvailableGroups([]);
    setSaving(false);
  };

  const handleOK = () => {
    closeModal();
  };

  // =================== DRAG & DROP FUNCTIONALITY ===================
  const handleDragStart = (e, item, source) => {
    setDraggedItem({
      item,
      source,
    });

    e.dataTransfer.setData(
      "text/plain",
      item
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, target) => {
    e.preventDefault();

    if (!draggedItem || !selectedAccount) {
      return;
    }

    const {
      item: groupName,
      source,
    } = draggedItem;

    if (source === target) {
      return;
    }

    const action =
      source === "available" &&
      target === "selected"
        ? "add"
        : "remove";

    setSaving(true);

    try {
      await updateGroupInBackend(
        groupName,
        selectedAccount.id,
        action
      );

      // Update UI immediately
      if (action === "add") {
        setSelectedGroups((prev) => [
          ...prev,
          groupName,
        ]);

        setAvailableGroups((prev) =>
          prev.filter(
            (g) => g !== groupName
          )
        );
      } else {
        setSelectedGroups((prev) =>
          prev.filter(
            (g) => g !== groupName
          )
        );

        setAvailableGroups((prev) => [
          ...prev,
          groupName,
        ]);
      }

      const newGroups =
        action === "add"
          ? [
              ...selectedGroups,
              groupName,
            ]
          : selectedGroups.filter(
              (g) => g !== groupName
            );

      const updatedAccount = {
        ...selectedAccount,
        groups: newGroups,
        selected: `${newGroups.length} Selected`,
        group: `(${newGroups.join(", ")})`,
      };

      setAccounts((prev) =>
        prev.map((a) =>
          a.id === selectedAccount.id
            ? updatedAccount
            : a
        )
      );

      setSelectedAccount(
        updatedAccount
      );

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Group ${
          action === "add"
            ? "added"
            : "removed"
        } successfully!`,
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    } finally {
      setSaving(false);
      setDraggedItem(null);
    }
  };

  // =================== REFRESH DATA FROM SERVER ===================
  const handleSave = async () => {
    try {
      const accountsUrl =
        "https://hms.backend.hiragroupcloud.com/api/account/account";

      let groupsUrl =
        "https://hms.backend.hiragroupcloud.com/api/prices/get";

      if (userLabId) {
        groupsUrl += `?userLabId=${encodeURIComponent(
          userLabId
        )}`;
      }

      const [
        accountsRes,
        groupsRes,
      ] = await Promise.all([
        fetch(accountsUrl),
        fetch(groupsUrl),
      ]);

      if (
        !accountsRes.ok ||
        !groupsRes.ok
      ) {
        throw new Error(
          "Failed to refresh"
        );
      }

      const accountsData =
        await accountsRes.json();

      const groupsRaw =
        await groupsRes.json();

      setAllAccounts(
        accountsData.data
      );

      let filteredAccounts =
        accountsData.data;

      if (userLabId) {
        filteredAccounts =
          accountsData.data.filter(
            (acc) =>
              acc.addedBy ===
              userLabId
          );
      }

      const formatted =
        filteredAccounts.map(
          (account) => {
            const accountGroups =
              account.priceGroups
                ? account.priceGroups
                    .filter(
                      (g) =>
                        g &&
                        g.name
                    )
                    .map(
                      (g) =>
                        g.name
                    )
                : [];

            return {
              id: account._id,
              name:
                account.account_name ||
                `Account ${account.accountId}`,
              accountId:
                account.accountId,
              selected: `${accountGroups.length} Selected`,
              group: `(${accountGroups.join(
                ", "
              )})`,
              groups: accountGroups,
            };
          }
        );

      setAccounts(formatted);

      setPriceGroups(
        groupsRaw.map((g) => ({
          ...g,
          members:
            Array.isArray(
              g.members
            )
              ? g.members
              : [],
        }))
      );

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Data refreshed and saved successfully!",
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Partial Success",
        text: "Changes saved locally, but could not refresh from server.",
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    }
  };

  // =================== LOADING STATE ===================
  if (loading) {
    return (
      <div className="assignprice-loading">
        Loading accounts and groups...
      </div>
    );
  }

  // =================== ERROR STATE ===================
  if (error) {
    return (
      <div className="assignprice-error">
        <div className="assignprice-error-message">
          Error: {error}
        </div>

        <button
          onClick={() =>
            window.location.reload()
          }
          className="assignprice-try-again"
        >
          Try Again
        </button>
      </div>
    );
  }

  // =================== MAIN RENDER ===================
  return (
    <>
      <Header />
      <Navvbar />

      <div className="assignprice-wrap">
        <div className="assignprice-row">

          {/* =================== SIDEBAR =================== */}
          <div className="assignprice-sidebar">
            <h2>Settings</h2>

            <div className="assignprice-sidebar-menu">
              {[
                {
                  to: "/Tabs",
                  label: "Lab Details",
                },
                {
                  to: "/",
                  label: "Store",
                },
                {
                  to: "/usersetting",
                  label: "Users",
                },
                {
                  to: "/",
                  label: "Permission Groups",
                },
                {
                  to: "/Account",
                  label: "Accounts",
                },
                {
                  to: "/Owerviews",
                  label: "Finances",
                },
                {
                  to: "/owersetting",
                  label: "Case Settings",
                },
              ].map((link, i) => (
                <div
                  key={i}
                  className="assignprice-sidebar-item"
                >
                  <Link
                    to={link.to}
                    className="assignprice-sidebar-link"
                  >
                    {link.label}
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* =================== MAIN CONTENT =================== */}
          <div className="assignprice-content">
            <h1>
              Assign Price List Group To Accounts
            </h1>

            <div className="assignprice-box">

              {/* Accounts List */}
              {accounts.length > 0 ? (
                accounts.map((acc) => (
                  <div
                    key={acc.id}
                    className="assignprice-account-row"
                  >
                    <span className="assignprice-account-name">
                      {acc.name} ({acc.accountId})
                    </span>

                    <span
                      onClick={() =>
                        handleLinkClick(acc)
                      }
                      className="assignprice-selected-link"
                    >
                      {acc.selected}
                    </span>

                    <span className="assignprice-account-groups">
                      {acc.group}
                    </span>
                  </div>
                ))
              ) : (
                <div className="assignprice-no-accounts">
                  {userLabId
                    ? `No accounts found for Lab ID: ${userLabId}. Total accounts in system: ${allAccounts.length}`
                    : "No accounts found"}
                </div>
              )}

              {/* Refresh Button */}
              <button
                onClick={handleSave}
                className="assignprice-refresh-btn"
              >
                <span className="assignprice-refresh-icon"></span>
                Refresh & Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =================== MODAL =================== */}
      {isModalOpen &&
        selectedAccount && (
          <div
            className="assignprice-modal-overlay"
            onClick={closeModal}
          >
            <div
              className="assignprice-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >
              {/* Modal Header */}
              <div className="assignprice-modal-header">
                <span className="assignprice-modal-title">
                  Select Pricelist Groups
                </span>

                <span
                  className="assignprice-modal-close"
                  onClick={closeModal}
                >
                  x
                </span>
              </div>

              <h4 className="assignprice-modal-heading">
                Assign Pricelist Groups For{" "}
                {selectedAccount.name} (
                {selectedAccount.accountId})
              </h4>

              <div className="assignprice-modal-grid">

                {/* Selected Groups */}
                <div>
                  <div className="assignprice-group-title">
                    Selected Pricelist Groups
                  </div>

                  <div
                    className="assignprice-group-box"
                    onDragOver={
                      handleDragOver
                    }
                    onDrop={(e) =>
                      handleDrop(
                        e,
                        "selected"
                      )
                    }
                  >
                    {selectedGroups.length >
                    0 ? (
                      selectedGroups.map(
                        (
                          group,
                          i
                        ) => (
                          <div
                            key={i}
                            draggable
                            onDragStart={(
                              e
                            ) =>
                              handleDragStart(
                                e,
                                group,
                                "selected"
                              )
                            }
                            className={`assignprice-group-item ${
                              group ===
                              "default"
                                ? "assignprice-default-group"
                                : ""
                            }`}
                          >
                            {group}{" "}
                            {group ===
                              "default" &&
                              " (default group)"}
                          </div>
                        )
                      )
                    ) : (
                      <div className="assignprice-empty-group">
                        No groups selected
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Groups */}
                <div>
                  <div className="assignprice-group-title">
                    Available Pricelist Groups
                  </div>

                  <div
                    className="assignprice-group-box"
                    onDragOver={
                      handleDragOver
                    }
                    onDrop={(e) =>
                      handleDrop(
                        e,
                        "available"
                      )
                    }
                  >
                    {availableGroups.length >
                    0 ? (
                      availableGroups.map(
                        (
                          group,
                          i
                        ) => (
                          <div
                            key={i}
                            draggable
                            onDragStart={(
                              e
                            ) =>
                              handleDragStart(
                                e,
                                group,
                                "available"
                              )
                            }
                            className="assignprice-group-item"
                          >
                            {group}
                          </div>
                        )
                      )
                    ) : (
                      <div className="assignprice-empty-group">
                        No groups available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="assignprice-modal-buttons">
                <button
                  onClick={closeModal}
                  disabled={saving}
                  className={`assignprice-modal-btn ${
                    saving
                      ? "assignprice-btn-disabled"
                      : ""
                  }`}
                >
                  Cancel
                </button>

                <button
                  onClick={handleOK}
                  disabled={saving}
                  className={`assignprice-modal-btn ${
                    saving
                      ? "assignprice-btn-disabled"
                      : ""
                  }`}
                >
                  {saving
                    ? "Saving..."
                    : "OK"}
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default AssignPrice;